// src/services/recipeService.js
import apiClient from "./api/client";
import ENDPOINTS from "./api/endpoints";

export const recipeService = {
  // =======================
  // RECIPE APIs
  // =======================

  /** GET /api/admin/recipe?filters... */
  getAll: async (filters = {}) => {
    try {
      const res = await apiClient.get(ENDPOINTS.RECIPE.GET_ALL, {
        params: filters,
      });
      return res;
    } catch (error) {
      console.error("Failed to fetch recipes:", error);
      throw error;
    }
  },

  /** GET /api/admin/recipe/{id} */
  getById: async (id) => {
    try {
      const res = await apiClient.get(ENDPOINTS.RECIPE.GET_BY_ID(id));
      return res;
    } catch (error) {
      console.error(`Failed to fetch recipe ${id}:`, error);
      throw error;
    }
  },

  /** GET /api/admin/recipe/menu-item/{menuItemId} */
  getByMenuItemId: async (menuItemId) => {
    try {
      const res = await apiClient.get(
        ENDPOINTS.RECIPE.GET_BY_MENU_ITEM(menuItemId)
      );
      return res;
    } catch (error) {
      console.error(
        `Failed to fetch recipe for menu item ${menuItemId}:`,
        error
      );
      throw error;
    }
  },

  /** POST /api/admin/recipe */
  create: async (data) => {
    try {
      const res = await apiClient.post(ENDPOINTS.RECIPE.CREATE, data);
      return res;
    } catch (error) {
      console.error("Failed to create recipe:", error);
      throw error;
    }
  },

/** PUT /api/admin/recipe/{id}  – full recipe update, replaces all ingredients */
update: async (id, data) => {
  try {
    const res = await apiClient.put(ENDPOINTS.RECIPE.UPDATE(id), data);
    return res;
  } catch (error) {
    console.error(`Failed to update recipe ${id}:`, error);
    throw error;
  }
},


  /** DELETE /api/admin/recipe/{id} */
  delete: async (id) => {
    try {
      const res = await apiClient.delete(ENDPOINTS.RECIPE.DELETE(id));
      return res;
    } catch (error) {
      console.error(`Failed to delete recipe ${id}:`, error);
      throw error;
    }
  },

  // =======================
  // INGREDIENT APIs
  // =======================

 /** PATCH /api/admin/recipe/{id}/ingredients – add or update ONE ingredient */
updateIngredients: async (recipeId, data) => {
  try {
    const res = await apiClient.patch(
      ENDPOINTS.RECIPE.UPDATE_INGREDIENTS(recipeId),
      data
    );
    return res;
  } catch (error) {
    console.error(
      `Failed to update ingredients for recipe ${recipeId}:`,
      error
    );
    throw error;
  }
},


  /** DELETE /api/admin/recipe/{recipeId}/ingredients/{ingredientId} */
deleteIngredient: async (recipeId, ingredientId) => {
  try {
    const res = await apiClient.delete(
      ENDPOINTS.RECIPE.DELETE_INGREDIENT(recipeId, ingredientId)
    );
    return res;
  } catch (error) {
    console.error(
      `Failed to delete ingredient ${ingredientId} from recipe ${recipeId}:`,
      error
    );
    throw error;
  }
},



  // =======================
  // INVENTORY ITEM APIs (from INVENTORY_ITEMS)
  // =======================

  /** GET /api/inventory/items */
  getInventoryItems: async (filters = {}) => {
    try {
      const res = await apiClient.get(
        ENDPOINTS.INVENTORY_ITEMS.GET_ALL,
        { params: filters }
      );
      return res;
    } catch (error) {
      console.error("Failed to fetch inventory items:", error);
      throw error;
    }
  },

  /** GET /api/inventory/items/{id} */
  getInventoryItemById: async (id) => {
    try {
      const res = await apiClient.get(
        ENDPOINTS.INVENTORY_ITEMS.GET_BY_ID(id)
      );
      return res;
    } catch (error) {
      console.error(`Failed to fetch inventory item ${id}:`, error);
      throw error;
    }
  },

  /** POST /api/inventory/items */
  createInventoryItem: async (data) => {
    try {
      const res = await apiClient.post(
        ENDPOINTS.INVENTORY_ITEMS.CREATE,
        data
      );
      return res;
    } catch (error) {
      console.error("Failed to create inventory item:", error);
      throw error;
    }
  },

  /** PUT /api/inventory/items/{id} */
  updateInventoryItem: async (id, data) => {
    try {
      const res = await apiClient.put(
        ENDPOINTS.INVENTORY_ITEMS.UPDATE(id),
        data
      );
      return res;
    } catch (error) {
      console.error(`Failed to update inventory item ${id}:`, error);
      throw error;
    }
  },

  /** DELETE /api/inventory/items/{id} */
  deleteInventoryItem: async (id) => {
    try {
      const res = await apiClient.delete(
        ENDPOINTS.INVENTORY_ITEMS.DELETE(id)
      );
      return res;
    } catch (error) {
      console.error(`Failed to delete inventory item ${id}:`, error);
      throw error;
    }
  },

  /** GET /api/inventory/items/summary */
  getInventorySummary: async (filters = {}) => {
    try {
      const res = await apiClient.get(
        ENDPOINTS.INVENTORY_ITEMS.SUMMARY,
        { params: filters }
      );
      return res;
    } catch (error) {
      console.error("Failed to fetch inventory summary:", error);
      throw error;
    }
  },

  /** GET /api/inventory/items/low-stock */
  getLowStockItems: async (filters = {}) => {
    try {
      const res = await apiClient.get(
        ENDPOINTS.INVENTORY_ITEMS.LOW_STOCK,
        { params: filters }
      );
      return res;
    } catch (error) {
      console.error("Failed to fetch low stock items:", error);
      throw error;
    }
  },

  // =======================
  // UOM APIs (from UOM)
  // =======================

  /** GET /api/inventory/uom */
  getUoms: async () => {
    try {
      const res = await apiClient.get(ENDPOINTS.UOM.GET_ALL);
      return res;
    } catch (error) {
      console.error("Failed to fetch UOMs:", error);
      throw error;
    }
  },

  /** POST /api/inventory/uom */
  createUom: async (data) => {
    try {
      const res = await apiClient.post(ENDPOINTS.UOM.CREATE, data);
      return res;
    } catch (error) {
      console.error("Failed to create UOM:", error);
      throw error;
    }
  },

  /** PUT /api/inventory/uom/{id} */
  updateUom: async (id, data) => {
    try {
      const res = await apiClient.put(ENDPOINTS.UOM.UPDATE(id), data);
      return res;
    } catch (error) {
      console.error(`Failed to update UOM ${id}:`, error);
      throw error;
    }
  },

  /** DELETE /api/inventory/uom/{id} */
  deleteUom: async (id) => {
    try {
      const res = await apiClient.delete(ENDPOINTS.UOM.DELETE(id));
      return res;
    } catch (error) {
      console.error(`Failed to delete UOM ${id}:`, error);
      throw error;
    }
  },
};

export default recipeService;
